import { Box, Button, Typography } from '@mui/material';
import { FileItemThumbnail } from '@provision/rich-text-editor';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { DynamicIcon } from 'src/components/DynamicIcon/dynamic-icon';
import { FormSelect, FormTextField } from 'src/components/Forms';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { SupportIssueType, useContactSupport } from 'src/graphql/entities/contact-support';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import {
  ContactSupportFormField,
  type ContactSupportFormValues,
} from './contact-support-form.types';
import { SearchConversationField } from './search-conversation-field';

const defaultValues: ContactSupportFormValues = {
  [ContactSupportFormField.IssueType]: SupportIssueType.GENERAL_QUESTION,
  [ContactSupportFormField.Subject]: '',
  [ContactSupportFormField.AdditionalContext]: '',
  [ContactSupportFormField.Attachments]: null,
  [ContactSupportFormField.LinkedConversation]: null,
};

const ISSUE_TYPE_OPTIONS: Option[] = Object.values(SupportIssueType)
  .sort()
  .map((issueType) => ({ id: issueType, value: issueType, label: issueType }));

const MAX_ATTACHMENTS = 3;

export const ContactSupportModal = () => {
  const { closeModal } = useModalMethods(Modals.ContactSupportModal);
  const { isOpened } = useModalState(Modals.ContactSupportModal);
  const { contactSupport } = useContactSupport();
  const [fileLimitExceeded, setFileLimitExceeded] = useState(false);

  const formMethods = useForm<ContactSupportFormValues>({
    defaultValues,
    mode: 'onTouched',
  });
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { isValid, isSubmitSuccessful },
  } = formMethods;

  const attachments = watch(ContactSupportFormField.Attachments);
  const issueType = watch(ContactSupportFormField.IssueType);

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset();
    }
  }, [isSubmitSuccessful, reset]);

  const onSubmit = useCallback(
    async (data: ContactSupportFormValues) => {
      closeModal();
      await contactSupport({
        ...data,
        linkedConversation: data.linkedConversation,
      });
    },
    [closeModal, contactSupport],
  );

  const handleClose = useCallback(() => {
    reset();
    setFileLimitExceeded(false);
    closeModal();
  }, [closeModal, reset]);

  const removeAttachment = useCallback(
    (index: number) => {
      if (!attachments) return;

      const newAttachments = Array.from(attachments);
      newAttachments.splice(index, 1);

      const dataTransfer = new DataTransfer();
      newAttachments.forEach((file) => {
        dataTransfer.items.add(file);
      });

      setValue(ContactSupportFormField.Attachments, dataTransfer.files);
      setFileLimitExceeded(false);
    },
    [attachments, setValue],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;

      const currentAttachments = attachments?.length || 0;
      if (currentAttachments + files.length > MAX_ATTACHMENTS) {
        setFileLimitExceeded(true);
        return;
      }

      const newAttachments = Array.from(files || []);

      // For some reason, passing the files directly to setValue doesn't work
      // so we have to create a new DataTransfer object and add the files to it
      const dataTransfer = new DataTransfer();
      Array.from(attachments || []).forEach((file) => {
        dataTransfer.items.add(file);
      });
      newAttachments.forEach((file) => {
        dataTransfer.items.add(file);
      });

      setValue(ContactSupportFormField.Attachments, dataTransfer.files);
      setFileLimitExceeded(false);
    },
    [attachments, setValue],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Contact Support"
      actions={[
        { label: 'Cancel', kind: 'secondary', testId: 'contact-support-cancel' },
        {
          label: 'Send request',
          kind: 'primary',
          onClick: handleSubmit(onSubmit),
          disabled: !isValid,
          testId: 'contact-support-submit',
        },
      ]}
      onClose={handleClose}
      data-testid="contact-support-modal"
    >
      <GenericForm<ContactSupportFormValues> formMethods={formMethods}>
        <FormSelect
          type="select"
          name={ContactSupportFormField.IssueType}
          label="Issue Type"
          required
          options={ISSUE_TYPE_OPTIONS}
          data-testid="contact-support-issue-type"
        />
        <FormTextField
          type="textfield"
          name={ContactSupportFormField.Subject}
          label="Subject"
          textFieldProps={{ inputProps: { 'data-testid': 'contact-support-subject-input' } }}
        />
        <FormTextField
          type="textfield"
          name={ContactSupportFormField.AdditionalContext}
          label="Additional Context"
          required="This field is required"
          showErrorText
          textFieldProps={{
            multiline: true,
            rows: 4,
            inputProps: { 'data-testid': 'contact-support-context-input' },
          }}
        />
        <SearchConversationField
          required={
            issueType === SupportIssueType.CONVERSATION_QA_REQUEST
              ? 'This field is required'
              : false
          }
        />
        <Button variant="text" component="label" sx={{ alignSelf: 'flex-start' }}>
          <DynamicIcon name="upload" />
          Attach a file
          <input
            type="file"
            hidden
            multiple
            data-testid="contact-support-attachment-input"
            {...register(ContactSupportFormField.Attachments)}
            onChange={handleFileChange}
          />
        </Button>
        {fileLimitExceeded ? (
          <Typography color="error" data-testid="contact-support-file-limit-error">
            You can only upload up to 3 files.
          </Typography>
        ) : null}
        {!!attachments?.length &&
          Array.from(attachments).map((file, index) => (
            <Box key={file.name + index.toString()} data-testid="contact-support-attachment">
              <FileItemThumbnail
                index={index}
                file={file}
                onDelete={(index) => removeAttachment(index)}
              />
            </Box>
          ))}
      </GenericForm>
    </ModalContainer>
  );
};
